if (!customElements.get("announcement-bar")) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.popupId = this.getAttribute("data-dismiss-id") ?? this.dataset.id;

      //Setup admin
    }
    connectedCallback() {
      this.open = true;
      this.carousel = this.closest("announcement-carousel");

      this.duringThemeEditor();

      this.height = 0;
      if (localStorage.getItem(`${this.popupId}-dismissed`) == true) {
        this.close();
      } else {
        window.addEventListener("DOMContentLoaded", () => {
          this.openIt();
        });
      }
    }

    openIt() {
      document.body.classList.add("announcement-open");
      this.handleNewSize();
      this.resizeObs = new ResizeObserver(this.handleResize.bind(this));
      this.resizeObs.observe(this);
      this.checkForButton();
    }

    duringThemeEditor() {
      if (window.Shopify.designMode) {
        this.parent = this.closest(".shopify-section");
        if (!this.parent) {
          return;
        }
        this.parent.addEventListener("shopify:section:load", () => {
          localStorage.removeItem(`${this.popupId}-dismissed`, true);
          this.openIt();
        });
      }
    }

    checkForButton() {
      if (!this.open) {
        return;
      }
      this.button = this.querySelector("[data-close]");
      if (this.button) {
        this.button.addEventListener("click", this.close.bind(this));
      }
    }

    handleNewSize() {
      if (!this.open) {
        return;
      }

      this.newHeight = this.getBoundingClientRect().height;
      return this.changeBodyOffset();
    }

    handleResize(entries) {
      entries.forEach((entry) => {
        if (entry.target == this) {
          if (!this.open) {
            return;
          }
          let newHeight = this.getBoundingClientRect().height;
          if (this.height != newHeight) {
            this.newHeight = newHeight;
            this.changeBodyOffset();
          }
        }
      });
    }

    changeBodyOffset() {
      if (!this.open) {
        return;
      }
      document.body.style.setProperty(
        "--announcement-bottom",
        this.newHeight - 1 + "px",
      );
      this.height = this.newHeight;
    }

    close() {
      this.open = false;
      this.resizeObs.disconnect();
      document.body.classList.remove("announcement-open");
      this.newHeight = 0;
      this.changeBodyOffset();
      this.classList.add("announcement-dismissed");
      localStorage.setItem(`${this.popupId}-dismissed`, true);
      window.removeEventListener("resize", this.handleResize.bind(this));

      // Notify carousel that a slide was dismissed
      if (this.carousel) {
        this.carousel.handleSlideDismissed(this);
      }
    }
  }

  customElements.define("announcement-bar", AnnouncementBar);
}

// Announcement Carousel
if (!customElements.get("announcement-carousel")) {
  class AnnouncementCarousel extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.autoplayInterval = null;
    }

    connectedCallback() {
      this.track = this.querySelector(".announcement-carousel__track");
      this.slides = Array.from(
        this.querySelectorAll(".announcement-carousel__slide"),
      );
      this.prevBtn = this.querySelector(".announcement-carousel__arrow--prev");
      this.nextBtn = this.querySelector(".announcement-carousel__arrow--next");
      this.speed = (parseInt(this.getAttribute("data-speed")) || 5) * 1000;

      // Filter out already dismissed slides
      this.filterDismissedSlides();

      if (this.visibleSlides.length === 0) {
        this.setAttribute("data-empty", "true");
        return;
      }

      this.setupNavigation();
      this.startAutoplay();
      this.setupPauseOnHover();
      this.updateNavigationState();
    }

    filterDismissedSlides() {
      this.visibleSlides = this.slides.filter((slide) => {
        const bar = slide.querySelector("announcement-bar");
        if (!bar) return true;
        const popupId = bar.getAttribute("data-dismiss-id") ?? bar.dataset.id;
        const isDismissed =
          localStorage.getItem(`${popupId}-dismissed`) == "true";
        if (isDismissed) {
          slide.classList.add("dismissed");
        }
        return !isDismissed;
      });
    }

    setupNavigation() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener("click", () => this.prev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener("click", () => this.next());
      }
    }

    setupPauseOnHover() {
      this.addEventListener("mouseenter", () => this.stopAutoplay());
      this.addEventListener("mouseleave", () => this.startAutoplay());
    }

    startAutoplay() {
      if (this.visibleSlides.length <= 1) return;
      this.stopAutoplay();
      this.autoplayInterval = setInterval(() => this.next(), this.speed);
    }

    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    }

    next() {
      if (this.visibleSlides.length <= 1) return;
      this.currentIndex = (this.currentIndex + 1) % this.visibleSlides.length;
      this.scrollToCurrentSlide();
    }

    prev() {
      if (this.visibleSlides.length <= 1) return;
      this.currentIndex =
        (this.currentIndex - 1 + this.visibleSlides.length) %
        this.visibleSlides.length;
      this.scrollToCurrentSlide();
    }

    scrollToCurrentSlide() {
      const slide = this.visibleSlides[this.currentIndex];
      if (slide && this.track) {
        slide.scrollIntoView({ behavior: "smooth", inline: "start" });
      }
    }

    updateNavigationState() {
      const singleSlide = this.visibleSlides.length <= 1;
      this.setAttribute("data-single-slide", singleSlide.toString());
    }

    handleSlideDismissed(bar) {
      const slide = bar.closest(".announcement-carousel__slide");
      if (slide) {
        slide.classList.add("dismissed");
      }

      // Update visible slides
      this.filterDismissedSlides();

      if (this.visibleSlides.length === 0) {
        this.setAttribute("data-empty", "true");
        this.stopAutoplay();
        return;
      }

      // Adjust current index if needed
      if (this.currentIndex >= this.visibleSlides.length) {
        this.currentIndex = 0;
      }

      this.updateNavigationState();
      this.scrollToCurrentSlide();

      // Restart autoplay if still multiple slides
      if (this.visibleSlides.length > 1) {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
    }
  }

  customElements.define("announcement-carousel", AnnouncementCarousel);
}
